import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { exportCalendarPdf, exportCalendarAgendaPdf } from "@/lib/export";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  CalendarDays, ChevronLeft, ChevronRight, Dumbbell, Trophy, Plus, FileDown,
  Eye, Trash2, Clock, MapPin, Shield, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  startOfMonth, startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";

/* Spanish weekday header (Mon → Sun). */
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(() => localStorage.getItem("cal_team_filter") || "all");
  const [kindFilter, setKindFilter] = useState(() => localStorage.getItem("cal_kind_filter") || "all");
  const [homeAway, setHomeAway] = useState("all");
  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { localStorage.setItem("cal_team_filter", teamId); }, [teamId]);
  useEffect(() => { localStorage.setItem("cal_kind_filter", kindFilter); }, [kindFilter]);
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState({ open: false, date: null });
  const [creating, setCreating] = useState(false);
  const [matchDialog, setMatchDialog] = useState({ open: false, ev: null });
  const [trainingDialog, setTrainingDialog] = useState({ open: false, ev: null });
  const [dayDialog, setDayDialog] = useState({ open: false, dateKey: null, events: [] });
  const [reloadKey, setReloadKey] = useState(0);

  /* Load teams (used for the team filter and to look up team names). */
  useEffect(() => {
    api.get("/teams")
      .then((r) => {
        const list = r.data || [];
        setTeams(list);
        setTeamId((cur) => (cur !== "all" && !list.some((t) => t.id === cur) ? "all" : cur));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  /* Load trainings + matches for the current selection. */
  useEffect(() => {
    setLoading(true);
    const params = teamId !== "all" ? `?team_id=${teamId}` : "";
    Promise.all([
      api.get(`/trainings${params}`),
      api.get(`/matches${params}`),
    ])
      .then(([t, m]) => {
        setTrainings(t.data || []);
        setMatches(m.data || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [teamId, reloadKey]);

  const teamName = useMemo(() => {
    const map = {};
    teams.forEach((t) => { map[t.id] = t.name; });
    return map;
  }, [teams]);

  const categories = useMemo(
    () => [...new Set(teams.map((t) => t.category).filter(Boolean))].sort(),
    [teams],
  );
  const allowedTeamIds = useMemo(
    () => new Set(
      teams
        .filter((t) => category === "all" || (t.category || "") === category)
        .map((t) => t.id),
    ),
    [teams, category],
  );

  /* Group events by "YYYY-MM-DD" for fast lookup while rendering the grid. */
  const eventsByDay = useMemo(() => {
    const out = {};
    const add = (iso, ev) => {
      const key = String(iso).slice(0, 10);
      if (!key) return;
      if (!out[key]) out[key] = [];
      out[key].push(ev);
    };
    trainings.forEach((t) => {
      if (kindFilter === "match" || homeAway !== "all") return;
      if (!allowedTeamIds.has(t.team_id)) return;
      const d = String(t.date).slice(0, 10);
      if ((dateFrom && d < dateFrom) || (dateTo && d > dateTo)) return;
      add(t.date, {
        kind: "training", id: t.id, date: t.date, notes: t.notes, time: t.time,
        team_id: t.team_id, team_name: teamName[t.team_id] || "",
      });
    });
    matches.forEach((m) => {
      if (kindFilter === "training") return;
      if (homeAway !== "all" && (m.home_away || "") !== homeAway) return;
      if (!allowedTeamIds.has(m.team_id)) return;
      const d = String(m.date).slice(0, 10);
      if ((dateFrom && d < dateFrom) || (dateTo && d > dateTo)) return;
      add(m.date, {
        kind: "match", id: m.id, date: m.date, rival: m.rival,
        home_away: m.home_away, result: m.result,
        kickoff_time: m.kickoff_time, team_id: m.team_id,
        team_name: teamName[m.team_id] || "",
        rffm_match_id: m.rffm_match_id, rffm_last_sync: m.rffm_last_sync,
        rffm_estado: m.rffm_estado,
      });
    });
    return out;
  }, [trainings, matches, teamName, kindFilter, homeAway, allowedTeamIds, dateFrom, dateTo]);

  /* Compute the 6-week grid (Mon-first) containing the current cursor month. */
  const gridDays = useMemo(() => {
    const first = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const last = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = [];
    let d = first;
    while (d <= last) {
      days.push(d);
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    return days;
  }, [cursor]);

  const monthLabel = format(cursor, "MMMM yyyy", { locale: es });
  const today = new Date();

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-purple-600" />
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Nunito" }}>
            Calendario
          </h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          {user?.role === "admin" && teams.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-slate-500">Equipo</span>
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger className="w-56" data-testid="calendar-team-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los equipos</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {categories.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-slate-500">Categoría</span>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-56" data-testid="calendar-category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500">Mostrar</span>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-56" data-testid="calendar-kind-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo</SelectItem>
                <SelectItem value="training">Solo entrenamientos</SelectItem>
                <SelectItem value="match">Solo partidos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-500">Partidos</span>
            <Select value={homeAway} onValueChange={setHomeAway}>
              <SelectTrigger className="w-56" data-testid="calendar-homeaway-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Casa y fuera</SelectItem>
                <SelectItem value="casa">Solo en casa</SelectItem>
                <SelectItem value="fuera">Solo fuera de casa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="text-xs uppercase tracking-widest text-slate-500">Fechas</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-1.5 rounded-md border border-slate-300 text-sm"
              data-testid="calendar-date-from"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-1.5 rounded-md border border-slate-300 text-sm"
              data-testid="calendar-date-to"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="text-xs font-bold text-purple-700 hover:text-purple-900"
                data-testid="calendar-date-clear"
              >
                ✕ Quitar
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => {
              const common = {
                team_id: teamId !== "all" ? teamId : "",
                kind: kindFilter !== "all" ? kindFilter : "",
                category: category !== "all" ? category : "",
                home_away: homeAway !== "all" ? homeAway : "",
              };
              if (dateFrom && dateTo) {
                exportCalendarAgendaPdf({ date_from: dateFrom, date_to: dateTo, ...common });
              } else if (dateFrom || dateTo) {
                toast.error("Completa las dos fechas (desde y hasta) para exportar el rango");
              } else {
                exportCalendarPdf({
                  year: cursor.getFullYear(),
                  month: cursor.getMonth() + 1,
                  ...common,
                });
              }
            }}
            data-testid="calendar-export-pdf"
          >
            <FileDown className="h-4 w-4 mr-1.5" />
            {dateFrom && dateTo ? "PDF del rango" : "PDF del mes"}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCursor(addMonths(cursor, -1))}
                data-testid="calendar-prev"
                aria-label="Mes anterior"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle
                className="text-lg font-black min-w-[10rem] text-center capitalize"
                data-testid="calendar-month-label"
              >
                {monthLabel}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCursor(addMonths(cursor, 1))}
                data-testid="calendar-next"
                aria-label="Mes siguiente"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCursor(startOfMonth(new Date()))}
                data-testid="calendar-today"
              >
                Hoy
              </Button>
              <div className="hidden lg:flex items-center gap-2.5 text-[11px] text-slate-600 flex-wrap justify-end max-w-md">
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-yellow-400" />
                  Entreno
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-purple-800" />
                  Casa
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-purple-400" />
                  Fuera
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-green-600" />
                  Victoria
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-orange-500" />
                  Empate
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm bg-red-600" />
                  Derrota
                </span>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-7 text-xs font-bold text-slate-500 uppercase tracking-widest pb-2 border-b border-slate-100">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center py-1">{w}</div>
            ))}
          </div>

          <div
            className="grid grid-cols-7 gap-px bg-slate-100 mt-px"
            data-testid="calendar-grid"
          >
            {gridDays.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const events = eventsByDay[key] || [];
              const outOfMonth = !isSameMonth(day, cursor);
              const isToday = isSameDay(day, today);
              const canAdd = user?.role === "admin" || !!user?.team_id;
              const hasTraining = events.some((e) => e.kind === "training");
              const hasMatch = events.some((e) => e.kind === "match");
              // Left-border stripe indicates the type of events that day at a glance.
              let stripe = "border-l-4 border-l-transparent";
              if (hasMatch) stripe = "border-l-4 border-l-purple-600";
              else if (hasTraining) stripe = "border-l-4 border-l-yellow-400";
              return (
                <div
                  key={key}
                  className={[
                    "bg-white p-1.5 min-h-[92px] md:min-h-[112px] flex flex-col gap-1 relative group",
                    stripe,
                    outOfMonth ? "opacity-40" : "",
                  ].join(" ")}
                  data-testid={`cal-cell-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={[
                        "text-xs tabular-nums font-bold w-6 h-6 grid place-items-center rounded-full",
                        isToday
                          ? "bg-purple-600 text-white ring-2 ring-purple-300"
                          : hasMatch
                            ? "bg-purple-100 text-purple-800"
                            : hasTraining
                              ? "bg-yellow-100 text-yellow-800"
                              : "text-slate-600",
                      ].join(" ")}
                    >
                      {format(day, "d")}
                    </span>
                    {canAdd && !outOfMonth && (
                      <button
                        type="button"
                        onClick={() => setCreateDialog({ open: true, date: key })}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-slate-400 hover:text-purple-700 p-0.5 -mr-0.5"
                        aria-label={`Añadir evento el ${key}`}
                        title="Añadir entrenamiento o partido"
                        data-testid={`cal-cell-add-${key}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 overflow-hidden">
                    {events.slice(0, 3).map((ev) => (
                      <EventChip
                        key={`${ev.kind}-${ev.id}`}
                        ev={ev}
                        showTeam={user?.role === "admin" && teamId === "all" && teams.length > 1}
                        onClick={() => {
                          if (ev.kind === "training") setTrainingDialog({ open: true, ev });
                          else setMatchDialog({ open: true, ev });
                        }}
                      />
                    ))}
                    {events.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setDayDialog({ open: true, dateKey: key, events })}
                        className="text-[10px] text-purple-700 font-bold pl-1 text-left hover:underline"
                        data-testid={`cal-cell-${key}-more`}
                      >
                        +{events.length - 3} más
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {loading && (
            <div className="text-center text-xs text-slate-400 italic py-3">Cargando eventos…</div>
          )}
        </CardContent>
      </Card>

      <DayEventsDialog
        open={dayDialog.open}
        dateKey={dayDialog.dateKey}
        events={dayDialog.events}
        onClose={() => setDayDialog({ open: false, dateKey: null, events: [] })}
        onPick={(ev) => {
          setDayDialog({ open: false, dateKey: null, events: [] });
          if (ev.kind === "training") setTrainingDialog({ open: true, ev });
          else setMatchDialog({ open: true, ev });
        }}
      />

      <TrainingInfoDialog
        open={trainingDialog.open}
        ev={trainingDialog.ev}
        canManage={user?.role === "admin" || (user?.team_ids || []).includes(trainingDialog.ev?.team_id)}
        onClose={() => setTrainingDialog({ open: false, ev: null })}
        onView={() => {
          const id = trainingDialog.ev?.id;
          setTrainingDialog({ open: false, ev: null });
          if (id) navigate(`/trainings/${id}`);
        }}
        onSaved={(t) => {
          setTrainingDialog((s) => ({ ...s, ev: { ...s.ev, notes: t.notes, time: t.time } }));
          setReloadKey((k) => k + 1);
        }}
        onDelete={async () => {
          const ev = trainingDialog.ev;
          if (!ev) return;
          if (!window.confirm(`¿Eliminar el entrenamiento del ${String(ev.date).slice(0, 10)}?`)) return;
          try {
            await api.delete(`/trainings/${ev.id}`);
            toast.success("Entrenamiento eliminado");
            setTrainingDialog({ open: false, ev: null });
            setReloadKey((k) => k + 1);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />

      <MatchInfoDialog
        open={matchDialog.open}
        ev={matchDialog.ev}
        canDelete={user?.role === "admin" || (user?.team_ids || []).includes(matchDialog.ev?.team_id)}
        onClose={() => setMatchDialog({ open: false, ev: null })}
        onView={() => {
          const id = matchDialog.ev?.id;
          setMatchDialog({ open: false, ev: null });
          if (id) navigate(`/matches/${id}`);
        }}
        onSaved={(m) => {
          setMatchDialog((s) => ({
            ...s,
            ev: {
              ...s.ev, rival: m.rival, home_away: m.home_away,
              kickoff_time: m.kickoff_time, result: m.result, date: m.date,
            },
          }));
          setReloadKey((k) => k + 1);
        }}
        onDelete={async () => {
          const ev = matchDialog.ev;
          if (!ev) return;
          if (!window.confirm(`¿Eliminar el partido contra ${ev.rival}?`)) return;
          try {
            await api.delete(`/matches/${ev.id}`);
            toast.success("Partido eliminado");
            setMatchDialog({ open: false, ev: null });
            setReloadKey((k) => k + 1);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />

      <CreateEventDialog
        open={createDialog.open}
        date={createDialog.date}
        teams={teams}
        defaultTeamId={teamId !== "all" ? teamId : ((user?.team_ids && user.team_ids[0]) || user?.team_id || (teams[0]?.id))}
        creating={creating}
        onClose={() => setCreateDialog({ open: false, date: null })}
        onSubmit={async (payload) => {
          setCreating(true);
          try {
            if (payload.kind === "training") {
              const dates = payload.dates || [payload.date];
              if (dates.length > 1) {
                for (const d of dates) {
                  await api.post("/trainings", { team_id: payload.team_id, date: d, time: payload.time });
                }
                toast.success(`${dates.length} entrenamientos creados`);
                setCreateDialog({ open: false, date: null });
                setReloadKey((k) => k + 1);
              } else {
                const { data } = await api.post("/trainings", {
                  team_id: payload.team_id,
                  date: dates[0],
                  time: payload.time,
                });
                toast.success("Entrenamiento creado");
                setCreateDialog({ open: false, date: null });
                navigate(`/trainings/${data.id}`);
              }
            } else {
              const { data } = await api.post("/matches", {
                team_id: payload.team_id,
                date: payload.date,
                rival: payload.rival,
                home_away: payload.home_away,
              });
              toast.success("Partido creado");
              setCreateDialog({ open: false, date: null });
              navigate(`/matches/${data.id}`);
            }
          } catch (e) {
            toast.error(formatApiError(e));
          } finally {
            setCreating(false);
          }
        }}
      />
    </div>
  );
}

/* Classify "X-Y" result vs home/away as W/D/L for our team. */
function matchOutcome(ev) {
  const raw = (ev.result || "").trim().replace("–", "-");
  const parts = raw.split("-").map((s) => s.trim());
  if (parts.length !== 2 || !/^\d+$/.test(parts[0]) || !/^\d+$/.test(parts[1])) return null;
  const a = parseInt(parts[0], 10);
  const b = parseInt(parts[1], 10);
  const gf = ev.home_away === "casa" ? a : b;
  const ga = ev.home_away === "casa" ? b : a;
  return gf > ga ? "W" : gf < ga ? "L" : "D";
}

function chipClasses(ev) {
  if (ev.kind === "training") return "bg-yellow-400 hover:bg-yellow-500 text-slate-900";
  const outcome = matchOutcome(ev);
  if (outcome === "W") return "bg-green-600 hover:bg-green-700 text-white";
  if (outcome === "D") return "bg-orange-500 hover:bg-orange-600 text-white";
  if (outcome === "L") return "bg-red-600 hover:bg-red-700 text-white";
  return ev.home_away === "casa"
    ? "bg-purple-800 hover:bg-purple-900 text-white"
    : "bg-purple-400 hover:bg-purple-500 text-white";
}

function MatchDetails({ ev }) {
  return (
    <div className="space-y-1.5 text-sm" data-testid={`match-hover-info-${ev.id}`}>
      <div className="flex items-center gap-2 font-black text-slate-900" style={{ fontFamily: "Nunito" }}>
        <Trophy className="h-4 w-4 text-purple-600 shrink-0" />
        {ev.home_away === "casa" ? "vs" : "@"} {ev.rival}
      </div>
      {ev.team_name && (
        <div className="flex items-center gap-2 text-slate-600">
          <Shield className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          {ev.team_name}
        </div>
      )}
      <div className="flex items-center gap-2 text-slate-600">
        <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        {ev.home_away === "casa" ? "En casa 🏠" : "Fuera ✈"}
      </div>
      <div className="flex items-center gap-2 text-slate-600">
        <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        {ev.kickoff_time ? `${ev.kickoff_time} h` : "Hora sin asignar"}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Resultado</span>
        {ev.result ? (
          <span className="font-black text-purple-700 tabular-nums">{ev.result}</span>
        ) : (
          <span className="text-slate-400 italic text-xs">Sin resultado</span>
        )}
      </div>
    </div>
  );
}

function EventChip({ ev, onClick, showTeam }) {
  const isTraining = ev.kind === "training";
  const Icon = isTraining ? Dumbbell : Trophy;
  const classes = chipClasses(ev);
  let label;
  if (isTraining) {
    label = (ev.time ? `${ev.time} · ` : "") + (ev.team_name || "Entreno");
  } else {
    const time = ev.kickoff_time ? `${ev.kickoff_time} · ` : "";
    label = `${time}${ev.home_away === "casa" ? "vs" : "@"} ${ev.rival}`;
  }
  // Compact team-abbreviation badge (first 3 letters) shown when Director
  // deportivo has "all teams" selected — lets him tell which team the event
  // belongs to at a glance.
  const teamAbbr = ev.team_name
    ? ev.team_name.replace(/[^A-Za-zÀ-ÿ0-9 ]+/g, "").split(/\s+/).slice(0, 2)
        .map((w) => w[0]).join("").slice(0, 3).toUpperCase()
    : "";
  const chip = (
    <button
      onClick={onClick}
      className={`text-[10px] leading-tight text-left rounded px-1.5 py-0.5 truncate inline-flex items-center gap-1 w-full ${classes}`}
      title={isTraining ? label + (ev.team_name ? ` — ${ev.team_name}` : "") : undefined}
      data-testid={`cal-chip-${ev.kind}-${ev.id}`}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" />
      {showTeam && teamAbbr && (
        <span className="text-[9px] font-bold bg-white/25 rounded px-1 leading-3">
          {teamAbbr}
        </span>
      )}
      <span className="truncate">{label}</span>
    </button>
  );
  if (isTraining) return chip;
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>{chip}</HoverCardTrigger>
      <HoverCardContent side="top" className="w-64 p-3">
        <MatchDetails ev={ev} />
        <div className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
          Pincha en el icono para ver o eliminar el partido.
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function DayEventsDialog({ open, dateKey, events, onClose, onPick }) {
  const matches = events.filter((e) => e.kind === "match");
  const trainings = events.filter((e) => e.kind === "training");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="day-events-dialog">
        <DialogHeader>
          <DialogTitle>Eventos del día</DialogTitle>
          {dateKey && (
            <DialogDescription className="capitalize">
              {format(new Date(`${dateKey}T00:00:00`), "EEEE d 'de' MMMM yyyy", { locale: es })}
              {" · "}{events.length} evento{events.length !== 1 ? "s" : ""}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {matches.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Partidos</div>
              {matches.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => onPick(ev)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity ${chipClasses(ev)}`}
                  data-testid={`day-event-match-${ev.id}`}
                >
                  <Trophy className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {ev.kickoff_time ? `${ev.kickoff_time} · ` : ""}
                    {ev.home_away === "casa" ? "vs" : "@"} {ev.rival}
                    {ev.result ? ` (${ev.result})` : ""}
                  </span>
                  <span className="ml-auto text-[10px] font-bold opacity-80 shrink-0">
                    {ev.home_away === "casa" ? "🏠 Casa" : "✈ Fuera"}
                    {ev.team_name ? ` · ${ev.team_name}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
          {trainings.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Entrenamientos</div>
              {trainings.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => onPick(ev)}
                  className="w-full text-left rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 transition-colors"
                  data-testid={`day-event-training-${ev.id}`}
                >
                  <Dumbbell className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {ev.time ? `${ev.time} · ` : ""}Entrenamiento{ev.notes ? ` · ${ev.notes}` : ""}
                  </span>
                  {ev.team_name && (
                    <span className="ml-auto text-[10px] font-bold opacity-80 shrink-0">{ev.team_name}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="text-[11px] text-slate-400">
          Pincha en un evento para ver, editar o eliminar.
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrainingInfoDialog({ open, ev, canManage, onClose, onView, onSaved, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullTraining, setFullTraining] = useState(null);
  const [notes, setNotes] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setFullTraining(null);
    }
  }, [open]);

  const startEdit = async () => {
    try {
      const { data } = await api.get(`/trainings/${ev.id}/attendance`);
      setFullTraining(data.training);
      setNotes(data.training.notes || "");
      setTime(data.training.time || "");
      setEditing(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/trainings/${ev.id}`, {
        ...fullTraining,
        notes: notes.trim(),
        time,
      });
      toast.success("Entrenamiento actualizado");
      setEditing(false);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="training-info-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar entrenamiento" : "Información del entrenamiento"}</DialogTitle>
          {ev?.date && (
            <DialogDescription className="capitalize">
              {format(new Date(`${String(ev.date).slice(0, 10)}T00:00:00`), "EEEE d 'de' MMMM yyyy", { locale: es })}
            </DialogDescription>
          )}
        </DialogHeader>

        {!editing && ev && (
          <div className="space-y-1.5 text-sm" data-testid="training-dialog-info">
            <div className="flex items-center gap-2 font-black text-slate-900" style={{ fontFamily: "Nunito" }}>
              <Dumbbell className="h-4 w-4 text-yellow-500 shrink-0" />
              Entrenamiento
            </div>
            {ev.team_name && (
              <div className="flex items-center gap-2 text-slate-600">
                <Shield className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                {ev.team_name}
              </div>
            )}
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              {ev.time ? `${ev.time} h` : "Hora sin asignar"}
            </div>
            <div className="flex items-start gap-2">
              <span className="text-slate-500 text-xs uppercase tracking-widest font-semibold pt-0.5">Notas</span>
              {ev.notes ? (
                <span className="text-slate-700">{ev.notes}</span>
              ) : (
                <span className="text-slate-400 italic text-xs pt-0.5">Sin notas</span>
              )}
            </div>
          </div>
        )}

        {editing && (
          <div className="space-y-3" data-testid="training-edit-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Hora</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="training-edit-time"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Notas</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Notas del entrenamiento…"
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="training-edit-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="training-edit-cancel">
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="training-edit-save"
              >
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </>
          ) : (
            <>
              {canManage && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={onDelete}
                  data-testid="training-dialog-delete"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar
                </Button>
              )}
              {canManage && (
                <Button
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={startEdit}
                  data-testid="training-dialog-edit"
                >
                  <Pencil className="h-4 w-4 mr-1.5" /> Editar
                </Button>
              )}
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={onView}
                data-testid="training-dialog-view"
              >
                <Eye className="h-4 w-4 mr-1.5" /> Ver entrenamiento
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchInfoDialog({ open, ev, canDelete, onClose, onView, onDelete, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullMatch, setFullMatch] = useState(null);
  const [form, setForm] = useState({ rival: "", home_away: "casa", kickoff_time: "", result: "", date: "" });

  useEffect(() => {
    if (!open) {
      setEditing(false);
      setFullMatch(null);
    }
  }, [open]);

  const startEdit = async () => {
    try {
      const { data } = await api.get(`/matches/${ev.id}/convocation`);
      const m = data.match;
      setFullMatch(m);
      setForm({
        rival: m.rival || "",
        home_away: m.home_away || "casa",
        kickoff_time: m.kickoff_time || "",
        result: m.result || "",
        date: (m.date || "").slice(0, 10),
      });
      setEditing(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    if (!form.rival.trim()) {
      toast.error("Escribe el nombre del rival");
      return;
    }
    if (!form.date) {
      toast.error("Selecciona la fecha del partido");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/matches/${ev.id}`, {
        ...fullMatch,
        rival: form.rival.trim(),
        home_away: form.home_away,
        kickoff_time: form.kickoff_time,
        result: form.result.trim(),
        date: form.date,
      });
      toast.success("Partido actualizado");
      setEditing(false);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="match-info-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar partido" : "Información del partido"}</DialogTitle>
          {ev?.date && (
            <DialogDescription className="capitalize">
              {format(new Date(`${String(ev.date).slice(0, 10)}T00:00:00`), "EEEE d 'de' MMMM yyyy", { locale: es })}
            </DialogDescription>
          )}
        </DialogHeader>

        {!editing && ev && <MatchDetails ev={ev} />}
        {!editing && ev?.rffm_estado && (
          <div className="text-[11px] font-black text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="calendar-rffm-estado">
            {ev.rffm_estado}
          </div>
        )}
        {!editing && ev?.rffm_match_id && (
          <div className="text-[10px] text-emerald-700 font-semibold" data-testid="calendar-rffm-badge">
            ✓ RFFM{ev.rffm_last_sync ? ` · Última sincronización: ${new Date(ev.rffm_last_sync).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
          </div>
        )}

        {editing && (
          <div className="space-y-3" data-testid="match-edit-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Fecha del partido</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="match-edit-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Rival</label>
              <input
                type="text"
                value={form.rival}
                onChange={(e) => setForm((f) => ({ ...f, rival: e.target.value }))}
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="match-edit-rival"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, home_away: "casa" }))}
                data-testid="match-edit-home"
                className={`py-2 rounded-md text-sm font-semibold border ${
                  form.home_away === "casa"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "border-slate-300 text-slate-600"
                }`}
              >
                🏠 En casa
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, home_away: "fuera" }))}
                data-testid="match-edit-away"
                className={`py-2 rounded-md text-sm font-semibold border ${
                  form.home_away === "fuera"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "border-slate-300 text-slate-600"
                }`}
              >
                ✈ Fuera
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Hora del partido</label>
                <input
                  type="time"
                  value={form.kickoff_time}
                  onChange={(e) => setForm((f) => ({ ...f, kickoff_time: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                  data-testid="match-edit-kickoff"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Resultado</label>
                <input
                  type="text"
                  value={form.result}
                  onChange={(e) => setForm((f) => ({ ...f, result: e.target.value }))}
                  placeholder="Ej. 3-1"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                  data-testid="match-edit-result"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="match-edit-cancel">
                Cancelar
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="match-edit-save"
              >
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </>
          ) : (
            <>
              {canDelete && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={onDelete}
                  data-testid="match-dialog-delete"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar partido
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={startEdit}
                  data-testid="match-dialog-edit"
                >
                  <Pencil className="h-4 w-4 mr-1.5" /> Editar
                </Button>
              )}
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={onView}
                data-testid="match-dialog-view"
              >
                <Eye className="h-4 w-4 mr-1.5" /> Ver partido
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WEEKDAY_OPTS = [
  { label: "L", dow: 1 }, { label: "M", dow: 2 }, { label: "X", dow: 3 },
  { label: "J", dow: 4 }, { label: "V", dow: 5 }, { label: "S", dow: 6 },
  { label: "D", dow: 0 },
];

function CreateEventDialog({ open, date, teams, defaultTeamId, creating, onClose, onSubmit }) {
  const [kind, setKind] = useState("training");
  const [teamId, setTeamId] = useState(defaultTeamId || "");
  const [rival, setRival] = useState("");
  const [homeAway, setHomeAway] = useState("casa");
  const [trainingTime, setTrainingTime] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [days, setDays] = useState([]);
  const [until, setUntil] = useState("");

  useEffect(() => {
    if (open) {
      setKind("training");
      setTeamId(defaultTeamId || "");
      setRival("");
      setHomeAway("casa");
      setRepeat(false);
      if (date) {
        const base = new Date(`${date}T00:00:00`);
        setDays([base.getDay()]);
        setUntil(format(endOfMonth(base), "yyyy-MM-dd"));
      } else {
        setDays([]);
        setUntil("");
      }
    }
  }, [open, defaultTeamId, date]);

  const label = date
    ? format(new Date(`${date}T00:00:00`), "EEEE d 'de' MMMM", { locale: es })
    : "";

  const submit = () => {
    if (!teamId) {
      toast.error("Selecciona un equipo");
      return;
    }
    if (kind === "match" && !rival.trim()) {
      toast.error("Escribe el nombre del rival");
      return;
    }
    let dates = [date];
    if (kind === "training" && repeat && days.length > 0 && until) {
      dates = [];
      let d = new Date(`${date}T00:00:00`);
      const end = new Date(`${until}T00:00:00`);
      while (d <= end) {
        if (days.includes(d.getDay())) dates.push(format(d, "yyyy-MM-dd"));
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      }
      if (dates.length === 0) {
        toast.error("No hay fechas que coincidan con los días elegidos");
        return;
      }
    }
    onSubmit({
      kind, team_id: teamId, date, dates,
      rival: rival.trim(), home_away: homeAway, time: trainingTime,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="calendar-create-dialog">
        <DialogHeader>
          <DialogTitle>Añadir al calendario</DialogTitle>
          <DialogDescription className="capitalize">{label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setKind("training")}
              data-testid="dialog-kind-training"
              className={[
                "flex flex-col items-center gap-1 py-3 rounded-lg border-2 transition-colors",
                kind === "training"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 text-slate-500 hover:border-slate-300",
              ].join(" ")}
            >
              <Dumbbell className="h-5 w-5" />
              <span className="text-sm font-semibold">Entrenamiento</span>
            </button>
            <button
              type="button"
              onClick={() => setKind("match")}
              data-testid="dialog-kind-match"
              className={[
                "flex flex-col items-center gap-1 py-3 rounded-lg border-2 transition-colors",
                kind === "match"
                  ? "border-purple-600 bg-purple-50 text-purple-800"
                  : "border-slate-200 text-slate-500 hover:border-slate-300",
              ].join(" ")}
            >
              <Trophy className="h-5 w-5" />
              <span className="text-sm font-semibold">Partido</span>
            </button>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Equipo
            </label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger data-testid="dialog-team-select">
                <SelectValue placeholder="Selecciona un equipo…" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {kind === "training" && (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Hora (opcional)</label>
              <input
                type="time"
                value={trainingTime}
                onChange={(e) => setTrainingTime(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                data-testid="dialog-training-time"
              />
            </div>
          )}

          {kind === "training" && (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={repeat}
                  onChange={(e) => setRepeat(e.target.checked)}
                  className="accent-purple-600 w-4 h-4"
                  data-testid="dialog-repeat-toggle"
                />
                Repetir semanalmente
              </label>
              {repeat && (
                <>
                  <div className="flex gap-1.5">
                    {WEEKDAY_OPTS.map((w) => (
                      <button
                        key={w.dow}
                        type="button"
                        onClick={() => setDays((ds) => (
                          ds.includes(w.dow) ? ds.filter((x) => x !== w.dow) : [...ds, w.dow]
                        ))}
                        data-testid={`dialog-repeat-day-${w.dow}`}
                        className={`w-8 h-8 rounded-full text-xs font-bold transition ${
                          days.includes(w.dow)
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Hasta</label>
                    <input
                      type="date"
                      value={until}
                      min={date || undefined}
                      onChange={(e) => setUntil(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                      data-testid="dialog-repeat-until"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {kind === "match" && (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Rival
                </label>
                <input
                  type="text"
                  value={rival}
                  onChange={(e) => setRival(e.target.value)}
                  placeholder="Nombre del equipo rival"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
                  data-testid="dialog-rival-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setHomeAway("casa")}
                  data-testid="dialog-home"
                  className={`py-2 rounded-md text-sm font-semibold border ${
                    homeAway === "casa"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "border-slate-300 text-slate-600"
                  }`}
                >
                  🏠 En casa
                </button>
                <button
                  type="button"
                  onClick={() => setHomeAway("fuera")}
                  data-testid="dialog-away"
                  className={`py-2 rounded-md text-sm font-semibold border ${
                    homeAway === "fuera"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "border-slate-300 text-slate-600"
                  }`}
                >
                  ✈ Fuera
                </button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel">
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={creating}
            className={kind === "training"
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-purple-600 hover:bg-purple-700 text-white"}
            data-testid="dialog-submit"
          >
            {creating ? "Creando…" : "Crear y abrir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
